import React from 'react';
import { Button, Select, Tag, Tooltip } from 'antd';
import { MinusSquareOutlined, PlusSquareOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const ALLOWED_MIGRATION_STATUSES = ['Done', 'To Do', 'Wont Do'];
const toolTipTextMap = {
  'gdpr-vl': {
    lastMigratedDate: "Date and time this vendor list was most recently migrated to Didomi. ",
    migrationAction: "Clicking the Didomi icon migrates the vendor list to Didomi. You can run the migration multiple times, but each run creates a duplicate vendor list in Didomi.",
    migrationStatus: "Manually updated to help track migration progress. It does not reflect the actual status in Didomi."
  },
  // Used by both U.S. Multi-State and Global Enterprise regulation lists.
  'regulation-vl': {
    lastMigratedDate: "Date and time this regulation was most recently migrated to Didomi. ",
    migrationAction: "Clicking the Didomi icon migrates the regulation to Didomi. You can run the migration multiple times, but each run creates a duplicate regulation in Didomi.",
    migrationStatus: "Manually updated to help track migration progress. It does not reflect the actual status in Didomi."
  }
};

export const createMigrationColumns = (onMigrateClick, onStatusChange, canEdit = true, keyForTooltips = 'gdpr-vl') => {
  const labelRender = (props) => {
    const {label, value} = props;
    const colorValue = value === "Done" ? 'success' : value === 'Wont Do' ? 'blue' : 'grey'
    return <Tag color={colorValue}>{label}</Tag>
  }
  const columns = [
    {
      title: (
        <span>
          Last Migrated{' '}
          <Tooltip title={toolTipTextMap[keyForTooltips]?.lastMigratedDate}>
            <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'lastMigratedDate',
      key: 'lastMigratedDate',
      align: 'center',
      render: (text, record) => record.lastMigratedDate ?  dayjs(record.lastMigratedDate).format('YYYY-MM-DD hh:mm A') : '',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
    },
  ];

  // The "Migrate to Didomi" (DI) action is rendered inline in the table's own
  // Actions column (matching the message list pattern), so it is no longer a
  // standalone migration column here. `onMigrateClick` is kept in the signature
  // for backwards compatibility with existing callers.

  columns.push({
    title: (
      <span>
        Migration Status{' '}
        <Tooltip title={toolTipTextMap[keyForTooltips]?.migrationStatus}>
          <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
        </Tooltip>
      </span>
    ),
    dataIndex: 'migrationStatus',
    key: 'migrationStatus',
    align: 'center',
    className: 'migration-status-column',
    render: (text, record) => (
      <Select
        defaultValue={(record && record.migrationStatus) || 'To Do'}
        options={ALLOWED_MIGRATION_STATUSES.map((status) => ({ value: status, label: status }))}
        style={{ width: 'fit-content' }}
        value={record.migrationStatus}
        disabled={!canEdit}
        onChange={(newStatus) => {
          if (canEdit) {
            onStatusChange(record, newStatus);
          }
        }}
      />
    ),
    onCell: () => ({ onClick: (e) => e.stopPropagation() }),
  });

  return columns;
};

export const getMigrationTableColumn = (expanded, onToggle, onMigrateClick, onStatusChange, canEdit = true, keyForTooltips = 'gdpr-vl') => {
  const childColumns = createMigrationColumns(onMigrateClick, onStatusChange, canEdit, keyForTooltips);
  return childColumns;
  return {
    title: (
      <>
        {!expanded ? (
          <PlusSquareOutlined onClick={() => onToggle('migration')} />
        ) : (
          <MinusSquareOutlined onClick={() => onToggle('migration')} />
        )}{' '}
        Migration
      </>
    ),
    key: 'migration',
    align: 'center',
    children: expanded ? childColumns : [],
    render:  (text, record) => (
     !expanded && canEdit ? <Button type='primary' onClick={(e) => { e.stopPropagation(); onMigrateClick(record); }}>DI</Button> : record?.migrationStatus
    ),
    onCell: !expanded && canEdit ? () => ({ onClick: (e) => e.stopPropagation() }) : undefined,
  };
};
